import { PAY_BREAKDOWN_SECTION, type PayBreakdownSection } from "./types";

export const PAY_BREAKDOWN_PATH = "/payroll-details";
export const PAY_BREAKDOWN_SHEET_PARAM = "sheet";
/**
 * Opens the workbook on a department rather than a specific tab, identified by its
 * access key.
 *
 * Deliberately not a sheet id: ids are regenerated every payroll run, so a link
 * mailed on Monday points at nothing after Tuesday's rerun. Access keys outlive
 * runs, which is also why approvals are keyed on them.
 */
export const PAY_BREAKDOWN_APPROVAL_UNIT_PARAM = "approval_unit";
/**
 * Opens the workbook on one tab, identified by its name.
 *
 * The third way to address a sheet, and the only one that always works. A sheet id
 * is regenerated by every run; an access key outlives runs but most businesses have
 * configured none. A name is what the manager reads on the tab, is generated the
 * same way each run, and is always there.
 */
export const PAY_BREAKDOWN_SHEET_NAME_PARAM = "sheet_name";

export const getPayBreakdownSheetId = (searchParams: URLSearchParams) =>
  searchParams.get(PAY_BREAKDOWN_SHEET_PARAM);

export const getPayBreakdownApprovalUnit = (searchParams: URLSearchParams) =>
  searchParams.get(PAY_BREAKDOWN_APPROVAL_UNIT_PARAM);

export const getPayBreakdownSheetName = (searchParams: URLSearchParams) =>
  searchParams.get(PAY_BREAKDOWN_SHEET_NAME_PARAM);

const PAY_BREAKDOWN_SECTIONS = Object.values(PAY_BREAKDOWN_SECTION);

const isPayBreakdownSection = (
  sectionPathValue: string | undefined,
): sectionPathValue is PayBreakdownSection =>
  !!sectionPathValue &&
  (PAY_BREAKDOWN_SECTIONS as readonly string[]).includes(sectionPathValue);

export const getPayBreakdownSection = (
  sectionPathValue: string | undefined,
): PayBreakdownSection =>
  isPayBreakdownSection(sectionPathValue)
    ? sectionPathValue
    : PAY_BREAKDOWN_SECTION.breakdown;

export const withPayBreakdownSheetId = (
  searchParams: URLSearchParams,
  sheetId: string,
) => {
  const nextSearchParams = new URLSearchParams(searchParams);
  nextSearchParams.set(PAY_BREAKDOWN_SHEET_PARAM, sheetId);
  return nextSearchParams;
};

export const isPayBreakdownPath = (pathname: string) => {
  const afterPath = pathname.slice(PAY_BREAKDOWN_PATH.length);
  return (
    pathname.startsWith(PAY_BREAKDOWN_PATH) &&
    (afterPath === "" || afterPath.startsWith("/"))
  );
};

export const getPayBreakdownPath = (
  periodStart?: string,
  section: PayBreakdownSection = PAY_BREAKDOWN_SECTION.breakdown,
) =>
  periodStart
    ? `${PAY_BREAKDOWN_PATH}/${periodStart}/${section}`
    : PAY_BREAKDOWN_PATH;

export const getPayBreakdownStatementPath = (
  periodStart: string,
  statementId: string,
) => `${PAY_BREAKDOWN_PATH}/${periodStart}/statements/${statementId}`;

/**
 * The Grid, opened on the sheet a question's pay was worked out on.
 *
 * By name, not id: every payroll run regenerates the workbook and its sheet ids with
 * it, so an id link points at nothing after a rerun — which is exactly when someone
 * follows this. An access key would also survive, but most businesses have
 * configured none, so it would leave the link missing where it is most needed.
 */
export const getPayBreakdownSheetPathForName = (
  periodStart: string,
  sheetName: string,
) =>
  `${getPayBreakdownPath(periodStart)}?${new URLSearchParams({
    [PAY_BREAKDOWN_SHEET_NAME_PARAM]: sheetName,
  }).toString()}`;

export const getPayBreakdownNotificationsPath = (periodStart: string) =>
  `${PAY_BREAKDOWN_PATH}/${periodStart}/notifications`;
